/**
 * A short trail of what the user did just before a crash, attached to every
 * report sent by `errorLog.ts`.
 *
 * This is the difference between a stack trace and a reproduction: the frame
 * says which line threw, the breadcrumbs say how the user got that line to run.
 *
 * PII rule, non-negotiable: this records element IDENTITY, never element VALUE.
 * A click stores `button "Apply now"`, never what the user typed into a field,
 * and request URLs are recorded as pathnames with the query string stripped.
 */

export type BreadcrumbType = 'navigation' | 'click' | 'request'

export type Breadcrumb = {
  type: BreadcrumbType
  /** Milliseconds since page load — relative, so it carries no wall-clock trace. */
  at: number
  detail: string
}

const MAX_BREADCRUMBS = 20
const MAX_DETAIL_LENGTH = 160

const buffer: Breadcrumb[] = []
let installed = false

export function addBreadcrumb(type: BreadcrumbType, detail: string): void {
  if (!detail) return

  buffer.push({
    type,
    at: typeof performance === 'undefined' ? 0 : Math.round(performance.now()),
    detail: detail.slice(0, MAX_DETAIL_LENGTH)
  })

  // Ring buffer: the events right before the crash are the useful ones.
  if (buffer.length > MAX_BREADCRUMBS) buffer.shift()
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...buffer]
}

/** Test seam. */
export function resetBreadcrumbs(): void {
  buffer.length = 0
  installed = false
}

/**
 * Names the thing that was clicked, using only attributes that describe what it
 * IS — tag, accessible name, href, control name. `value` is never read.
 */
export function describeElement(target: EventTarget | null): string | null {
  if (typeof Element === 'undefined' || !(target instanceof Element)) return null

  const el =
    target.closest('a, button, [role="button"], input, select, textarea, summary') ?? target
  const tag = el.tagName.toLowerCase()

  // `textContent`, not `innerText`: it exists on every element (including SVG,
  // a plausible click target inside an icon button), and it INCLUDES the
  // `.visually-hidden` text this site adds for descriptive link labels — which
  // is exactly the part that names what the control does.
  const label =
    el.getAttribute('aria-label') ??
    el.getAttribute('name') ??
    el.getAttribute('id') ??
    el.textContent ??
    ''

  const name = label.trim().replaceAll(/\s+/g, ' ').slice(0, 60)
  const href = el.getAttribute('href')

  if (name && href) return `${tag} "${name}" → ${href}`
  if (name) return `${tag} "${name}"`
  if (href) return `${tag} → ${href}`
  return tag
}

/** `POST /cms/api/enquiry-forms` — method and path only, query string dropped. */
function describeRequest(input: RequestInfo | URL, init?: RequestInit): string {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()

  try {
    return `${method} ${new URL(url, window.location.origin).pathname}`
  } catch {
    return `${method} ${String(url).split('?')[0]}`
  }
}

/**
 * Installs the click and network listeners. Called once from `AppShell`.
 *
 * The `fetch` wrapper only ever OBSERVES: it returns the original response and
 * re-throws the original error unchanged, so a bug in here cannot change how
 * any request in the app behaves.
 */
export function installBreadcrumbs(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  document.addEventListener(
    'click',
    event => {
      // A listener on every click in the app: it must be impossible for a
      // breadcrumb to break a user interaction.
      try {
        const detail = describeElement(event.target)
        if (detail) addBreadcrumb('click', detail)
      } catch {
        // Recording is optional; clicking is not.
      }
    },
    { capture: true, passive: true }
  )

  const originalFetch = window.fetch
  if (typeof originalFetch !== 'function') return

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const response = await originalFetch(input, init)
      // Only failures are recorded — a log of every successful request would
      // push the interesting events out of a 20-slot buffer.
      if (!response.ok) {
        addBreadcrumb('request', `${describeRequest(input, init)} → ${response.status}`)
      }
      return response
    } catch (error) {
      addBreadcrumb('request', `${describeRequest(input, init)} → network failure`)
      throw error
    }
  }
}
